/**
 * UranIA PA — Login Imprese, Cittadini e Innovatori (Fase 4 redesign
 * vetrina/accesso, 2026-09-01).
 *
 * Pubblico di destinatari/beneficiari delle politiche gestite dalla PA
 * (non gestori di fondi): rispetto a `/login/pa` (Fase 3) —
 *   - Solo SPID e CIE, niente CNS (riservata agli operatori PA).
 *   - Layout a colonna singola centrata (non split-screen con diagramma
 *     del ciclo di programmazione: quel diagramma parla il linguaggio
 *     dell'AdG/funzionario, non pertinente per questo pubblico — la
 *     spec di Fase 4 non lo richiede).
 *   - Avviso AI Act con testo diverso e deliberatamente più esplicito sui
 *     limiti del contenuto generato (non è parere legale, non garantisce
 *     esiti) — testo indipendente da `ai_disclosure.*`, non va allineato
 *     meccanicamente a quello di `/login/pa`.
 *   - Sottotitolo esplicita che il servizio è "messo a disposizione dalla
 *     tua Amministrazione" — rinforza che l'accesso esiste su mandato
 *     della PA, non come offerta autonoma di UranIA.
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createRoute, Link } from '@tanstack/react-router';
import type { JSX } from 'react';

import { SpidButton } from '../../components/auth/SpidButton.tsx';
import { AltIdentityButton } from '../../components/auth/AltIdentityButton.tsx';
import { ALT_IDENTITY_OPTIONS, type SpidIdpOption } from '../../lib/auth/idp-options.ts';
import { startFederatedLogin } from '../../lib/auth/login-actions.ts';
import { Route as PubblicoRoute } from '../_pubblico.tsx';

function LoginBeneficiariPage(): JSX.Element {
  const { t } = useTranslation();
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStart = useCallback(
    async (keycloakAlias: string, acrValues: string): Promise<void> => {
      setError(null);
      setRedirecting(true);
      try {
        await startFederatedLogin({ keycloakAlias, acrValues });
      } catch (err) {
        setRedirecting(false);
        setError(err instanceof Error ? err.message : t('common.error'));
      }
    },
    [t],
  );

  const handleSpid = useCallback(
    (option: SpidIdpOption): void => {
      void handleStart(option.keycloakAlias, 'L2');
    },
    [handleStart],
  );

  const handleCie = useCallback((): void => {
    void handleStart(ALT_IDENTITY_OPTIONS.cie.keycloakAlias, ALT_IDENTITY_OPTIONS.cie.acrValues);
  }, [handleStart]);

  return (
    <section
      className="urania-login-page d-flex align-items-center justify-content-center py-5"
      data-testid="login-beneficiari-page"
      style={{ minHeight: 'calc(100vh - 120px)', backgroundColor: '#f4f7fb' }}
    >
      <div className="w-100 px-4" style={{ maxWidth: 480 }}>
        <h1 className="h3 mb-2 text-center">Accedi ai servizi UranIA per te</h1>
        <p className="text-muted text-center mb-4">
          Un supporto messo a disposizione dalla tua Amministrazione per orientarti nei bandi e
          nella progettazione della tua proposta.
        </p>

        <div className="bg-white rounded-3 p-4 p-md-5 shadow-sm">
          {error !== null && (
            <div className="alert alert-danger mb-3" role="alert" data-testid="login-error">
              {error}
            </div>
          )}

          <div
            className="urania-login-options d-flex flex-column gap-3"
            data-testid="login-options"
          >
            <div className="urania-login-option" data-testid="login-option-spid">
              <p className="text-muted small mb-2">{t('login.beneficiari_spid_intro')}</p>
              <SpidButton onSelectIdp={handleSpid} disabled={redirecting} />
            </div>

            <div className="position-relative my-2 text-center">
              <hr className="m-0" />
              <span
                className="position-absolute top-50 start-50 translate-middle px-3 small text-muted"
                style={{ backgroundColor: '#fff' }}
              >
                {t('login.divider_or')}
              </span>
            </div>

            <div className="urania-login-option" data-testid="login-option-cie">
              <AltIdentityButton flavour="cie" onClick={handleCie} disabled={redirecting} />
            </div>
          </div>

          {/* Avviso AI Act — versione beneficiario, Fase 4 */}
          <div
            className="urania-login-ai-notice mt-4"
            role="note"
            aria-label="Avviso AI Act articolo 50"
            data-testid="login-beneficiari-ai-notice"
          >
            <strong>Avviso AI Act</strong>{' '}
            <span>
              Stai interagendo con un sistema basato su intelligenza artificiale, messo a
              disposizione dalla tua Amministrazione attraverso UranIA PA. La trasparenza
              nell'uso dell'IA è obbligatoria ai sensi del Regolamento UE 2024/1689 (AI Act, art.
              50) e della Legge 23 settembre 2025, n. 132 (L. 132/2025). I contenuti generati sono
              un supporto all'orientamento: non costituiscono parere legale né garanzia di
              ammissibilità o esito di un bando. Verifica sempre le fonti citate e, per decisioni
              rilevanti, rivolgiti alla tua Amministrazione o a un professionista abilitato.
            </span>{' '}
            <a
              href="https://www.normattiva.it/eli/id/2025/09/24/25G00138/sg"
              rel="noreferrer noopener external"
              target="_blank"
              data-testid="login-beneficiari-ai-notice-link"
            >
              Legge 132/2025 (Normattiva)
            </a>
          </div>

          <section
            className="urania-login-help mt-4 pt-3 border-top"
            aria-labelledby="urania-login-beneficiari-help-heading"
          >
            <h2 id="urania-login-beneficiari-help-heading" className="h6 mb-2">
              {t('login.help_heading')}
            </h2>
            <p className="small text-muted mb-0" data-testid="login-beneficiari-help-support">
              Per assistenza scrivi a supporto@urania-olosky.it
            </p>
          </section>
        </div>

        <div className="text-center">
          <Link to="/" className="d-inline-block mt-4 small">
            Torna alla home
          </Link>
        </div>
      </div>
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => PubblicoRoute,
  path: '/login/beneficiari',
  component: LoginBeneficiariPage,
});
